import React, { useState, useEffect, useMemo } from "react";
import usePersistentFilters from "../Common/usePersistentFilters";
import {
 Row,
 Col,
 Card,
 Table,
 Badge,
 Button,
 Form,
 Spinner,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import PageTitle from "../../layouts/PageTitle";
import api from "../../../api";
import { generateQCReport } from "../../../services/qcService";

const STATUS_COLORS = {
 Draft: "secondary",
 Submitted: "warning",
 Approved: "success",
 Rejected: "danger",
 Closed: "dark",
};

const RESULT_COLORS = {
 Pass: "success",
 Fail: "danger",
 "Conditional Pass": "warning",
 Pending: "secondary",
};

const INSPECTION_TYPES = [
 "Field Inspection",
 "Lab Test",
 "Material Inspection",
 "Process Inspection",
 "Final Inspection",
 "In-Process Inspection",
 "Third Party Inspection",
 "Other",
];

const QCReport = () => {
 const navigate = useNavigate();
 const [filters, setFilters] = usePersistentFilters("qc_report_filters", {
 workOrder: "",
 project: "",
 status: "",
 inspectionType: "",
 fromDate: "",
 toDate: "",
 });
 const [reportData, setReportData] = useState(null);
 const [loading, setLoading] = useState(false);
 const [projects, setProjects] = useState([]);
 const [workOrders, setWorkOrders] = useState([]);

 useEffect(() => {
 const load = async () => {
 try {
 const [pRes, woRes] = await Promise.all([
 api.get("/projects?limit=500"),
 api.get("/work-orders?limit=500"),
 ]);
 setProjects(pRes.data?.data || []);
 setWorkOrders(woRes.data?.data || []);
 } catch (_) {}
 };
 load();
 // Load default report on mount
 fetchReport({});
 }, []);

 const fetchReport = async (params) => {
 setLoading(true);
 try {
 const res = await generateQCReport(params);
 if (res.data.success) setReportData(res.data.data);
 } catch (_) {} finally {
 setLoading(false);
 }
 };

  const handleChange = (name, value) => {
    setFilters((prev) => {
      const next = { ...prev, [name]: value };
      if (name === "project") {
        if (value && prev.workOrder) {
          const currentWo = workOrders.find((w) => String(w._id) === String(prev.workOrder));
          const currentWoProjId = currentWo?.project?._id || currentWo?.project;
          if (currentWoProjId && String(currentWoProjId) !== String(value)) {
            next.workOrder = "";
          }
        }
      } else if (name === "workOrder") {
        if (value) {
          const selectedWo = workOrders.find((w) => String(w._id) === String(value));
          const autoProjId = selectedWo?.project?._id || selectedWo?.project;
          if (autoProjId) {
            next.project = String(autoProjId);
          }
        }
      }
      return next;
    });
  };

  const filteredWorkOrders = useMemo(() => {
    if (!filters.project) return workOrders;
    return workOrders.filter((wo) => {
      const pid = wo.project?._id || wo.project;
      return String(pid) === String(filters.project);
    });
  }, [workOrders, filters.project]);

 const handleGenerate = () => {
 const params = Object.fromEntries(
 Object.entries(filters).filter(([, v]) => v)
 );
 fetchReport(params);
 };

 const handlePrint = () => {
 window.print();
 };

 const summary = reportData?.summary;

 return (
 <div>
 <PageTitle activeMenu="QC Report" motherMenu="QC" />

 {/* Filter Bar */}
 <Card className="shadow-sm mb-3 d-print-none">
 <Card.Header className="d-flex justify-content-between align-items-center">
 <h6 className="mb-0">
 <i className="fas fa-filter me-2 text-primary" />
 Report Filters
 </h6>
 <Button
 variant="outline-secondary"
 size="sm"
 onClick={() => navigate(-1)}
 >
 <i className="fas fa-arrow-left me-1" /> Back
 </Button>
 </Card.Header>
 <Card.Body>
 <Row className="g-2 align-items-end">
 <Col md={3}>
 <Form.Label className="fw-semibold fs-12">Project</Form.Label>
 <Form.Select
 size="sm"
 value={filters.project}
 onChange={(e) => handleChange("project", e.target.value)}
 >
 <option value="">All Projects</option>
 {projects.map((p) => (
 <option key={p._id} value={p._id}>
 {p.projectName || p.name}
 </option>
 ))}
 </Form.Select>
 </Col>
 <Col md={3}>
 <Form.Label className="fw-semibold fs-12">Work Order</Form.Label>
 <Form.Select
 size="sm"
 value={filters.workOrder}
 onChange={(e) => handleChange("workOrder", e.target.value)}
 >
 <option value="">All Work Orders</option>
              {filteredWorkOrders.map((wo) => (
                <option key={wo._id} value={wo._id}>
                  {wo.woNo}{wo.label ? ` – ${wo.label}` : ""}
                  {!filters.project && (wo.project?.projectName || wo.project?.name) ? ` (${wo.project.projectName || wo.project.name})` : ""}
                </option>
              ))}
 </Form.Select>
 </Col>
 <Col md={2}>
 <Form.Label className="fw-semibold fs-12">Status</Form.Label>
 <Form.Select
 size="sm"
 value={filters.status}
 onChange={(e) => handleChange("status", e.target.value)}
 >
 <option value="">All</option>
 {["Draft", "Submitted", "Approved", "Rejected", "Closed"].map((s) => (
 <option key={s} value={s}>{s}</option>
 ))}
 </Form.Select>
 </Col>
 <Col md={2}>
 <Form.Label className="fw-semibold fs-12">Type</Form.Label>
 <Form.Select
 size="sm"
 value={filters.inspectionType}
 onChange={(e) => handleChange("inspectionType", e.target.value)}
 >
 <option value="">All Types</option>
 {INSPECTION_TYPES.map((t) => (
 <option key={t} value={t}>{t}</option>
 ))}
 </Form.Select>
 </Col>
 <Col md={2}>
 <Form.Label className="fw-semibold fs-12">From Date</Form.Label>
 <Form.Control
 size="sm"
 type="date"
 value={filters.fromDate}
 onChange={(e) => handleChange("fromDate", e.target.value)}
 />
 </Col>
 <Col md={2}>
 <Form.Label className="fw-semibold fs-12">To Date</Form.Label>
 <Form.Control
 size="sm"
 type="date"
 value={filters.toDate}
 onChange={(e) => handleChange("toDate", e.target.value)}
 />
 </Col>
 <Col md="auto" className="d-flex gap-2">
 <Button variant="primary" size="sm" onClick={handleGenerate} disabled={loading}>
 {loading ? <Spinner size="sm" /> : <><i className="fas fa-search me-1" />Generate</>}
 </Button>
 {reportData && (
 <Button variant="outline-secondary" size="sm" onClick={handlePrint}>
 <i className="fas fa-print me-1" /> Print
 </Button>
 )}
 </Col>
 </Row>
 </Card.Body>
 </Card>

 {loading && (
 <div className="text-center py-5">
 <Spinner animation="border" variant="primary" />
 <p className="mt-2 text-muted">Generating report…</p>
 </div>
 )}

 {reportData && !loading && (
 <>
 {/* Summary Cards */}
 <Row className="mb-4">
 {[
 { label: "Total Inspections", value: summary.total, color: "primary" },
 { label: "Pass", value: summary.pass, color: "success" },
 { label: "Fail", value: summary.fail, color: "danger" },
 { label: "NCR Raised", value: summary.ncr, color: "warning" },
 {
 label: "Pass Rate",
 value: `${summary.passRate}%`,
 color: summary.passRate >= 90 ? "success" : summary.passRate >= 70 ? "warning" : "danger",
 },
 ].map((s) => (
 <Col key={s.label} xs={6} md className="mb-3">
 <Card className={`border-${s.color} shadow-sm`}>
 <Card.Body className="p-3 text-center">
 <h4 className={`mb-0 text-${s.color} fw-bold`}>{s.value}</h4>
 <p className="mb-0 text-muted" style={{ fontSize: "0.78rem" }}>
 {s.label}
 </p>
 </Card.Body>
 </Card>
 </Col>
 ))}
 </Row>

 {/* Report Table */}
 <Card className="shadow-sm">
 <Card.Header className="d-flex justify-content-between">
 <h6 className="mb-0">
 <i className="fas fa-table me-2 text-primary" />
 QC Inspection Records ({reportData.records.length})
 </h6>
 <small className="text-muted">
 Generated: {new Date(reportData.generatedAt).toLocaleString("en-IN")}
 </small>
 </Card.Header>
 <Card.Body className="p-0">
 {reportData.records.length === 0 ? (
 <div className="text-center py-5 text-muted">
 No records match the selected filters.
 </div>
 ) : (
 <div className="table-responsive">
 <Table hover size="sm" className="mb-0 align-middle">
 <thead className="table-light">
 <tr>
 <th>#</th>
 <th>QC No</th>
 <th>Date</th>
 <th>Type</th>
 <th>Activity / Scope</th>
 <th>Location</th>
 <th>Work Order</th>
 <th>Obs.</th>
 <th>Result</th>
 <th>NCR</th>
 <th>Status</th>
 <th>Inspected By</th>
 </tr>
 </thead>
 <tbody>
 {reportData.records.map((rec, i) => (
 <tr key={rec._id}>
 <td>{i + 1}</td>
 <td>
 <strong className="text-primary">{rec.qcNumber}</strong>
 </td>
 <td>
 {new Date(rec.inspectionDate).toLocaleDateString("en-IN")}
 </td>
 <td>
 <span className="badge bg-light text-dark border" style={{ fontSize: "0.7rem" }}>
 {rec.inspectionType}
 </span>
 </td>
 <td>{rec.activityOrScope}</td>
 <td>{rec.location || "—"}</td>
 <td>
 <span className="text-primary small">
 {rec.workOrder?.woNo || "—"}
 </span>
 </td>
 <td className="text-center">{rec.observations?.length || 0}</td>
 <td>
 <Badge bg={RESULT_COLORS[rec.overallResult] || "secondary"}>
 {rec.overallResult}
 </Badge>
 </td>
 <td>
 {rec.nonConformanceRaised ? (
 <Badge bg="danger">Yes</Badge>
 ) : (
 <span className="text-muted">No</span>
 )}
 </td>
 <td>
 <Badge bg={STATUS_COLORS[rec.status] || "secondary"}>
 {rec.status}
 </Badge>
 </td>
 <td>{rec.inspectedBy?.name || "—"}</td>
 </tr>
 ))}
 </tbody>
 </Table>
 </div>
 )}
 </Card.Body>
 </Card>
 </>
 )}
 </div>
 );
};

export default QCReport;
